import React, {useState, useEffect, useCallback} from 'react'
import {Spinner} from '@material-tailwind/react'
import { debounce } from 'lodash';
import toast, { Toaster } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowRightIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from '@heroicons/react/24/solid';

import { encodedAddressPrefix, tippingAddress, ownerAddress, royalty, tip, contentLength, serviceFeeRate, padding, brc20ClaimFeeAddress, brc20ClaimOwnerAddress } from '../configs/constant';
import { Input } from '@material-tailwind/react'
import { formatAddress } from '../util/format-data'
import { getSignMintText, updatePayment, latestPayment, registerToken } from '../util/api'
import { getAsciiSum } from '../util/format-data';
import { getFeeRate, bytesToHex, buf2hex, textToHex, hexToBytes, getMempoolUtxos, loopTilAddressReceivesMoney, waitSomeSeconds, addressReceivedMoneyInThisTx, pushBTCpmt, calculateFee, getData, isValidTaprootAddress} from '../util/inscribe-util';
import { sendBtcTransaction } from 'sats-connect';
import FeeRateCard from '../components/FeeRateCard';
import { BitcoinNetworkType, signMessage, signTransaction } from 'sats-connect';
import { getAllBrc20ClaimData, updateBrc20ClaimData, getBrc20ClaimTransactionData } from '../util/api';
import { broadcastBitcoinTransaction } from '../util/api';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Slider,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter
} from "@material-tailwind/react";

import { getPATokenDetail } from '../util/api';

import { useWallet, useWallets } from '@wallet-standard/react';

export default function RecoverBrc20Claim() {
  const { wallets } = useWallets();

  const SatsConnectNamespace = 'sats-connect:';

  const isSatsConnectCompatibleWallet = (wallet) => {
      return SatsConnectNamespace in wallet.features;
  }

  const wallet = useSelector(state => state.wallet);

  const { Address, Script, Signer, Tap, Tx } = window.tapscript;

  const [loading, setLoading] = useState(false);

  const handleMint = async (mintData) => {
    const cryptoUtils = window.cryptoUtils;
    const KeyPair = cryptoUtils.KeyPair;
    
    const fees = {
      serviceFee: 5000,
      inscribeFee: Math.ceil((160 + 86 / 4) * mintData.feerate) + padding,
      transferFee: Math.floor(169 * mintData.feerate),
      totalFee: mintData.fundingAmount
    }

    let seckey = new KeyPair(mintData.privatekey);
    let pubkey = seckey.pub.rawX;

    const ec = new TextEncoder();

    const init_script = [
      pubkey,
      'OP_CHECKSIG'
    ];
    
    const init_script_backup = [
        '0x' + buf2hex(pubkey.buffer),
        'OP_CHECKSIG'
    ];

    let init_leaf = await Tap.tree.getLeaf(Script.encode(init_script));
    let [init_tapkey, init_cblock] = await Tap.getPubKey(pubkey, {target: init_leaf});

    const test_redeemtx = Tx.create({
      vin  : [{
          txid: 'a99d1112bcb35845fd44e703ef2c611f0360dd2bb28927625dbc13eab58cd968',
          vout: 0,
          prevout: {
              value: 10000,
              scriptPubKey: [ 'OP_1', init_tapkey ]
          },
      }],
      vout : [{
          value: 8000,
          scriptPubKey: [ 'OP_1', init_tapkey ]
      }],
    });
    
    const test_sig = await Signer.taproot.sign(seckey.raw, test_redeemtx, 0, {extension: init_leaf});
    test_redeemtx.vin[0].witness = [ test_sig.hex, init_script, init_cblock ];
    const isValid = await Signer.taproot.verify(test_redeemtx, 0, { pubkey });

    if(!isValid)
    {
      alert('Generated keys could not be validated. Please reload the app.');
      return;
    }

    let files = [];

    let mimetype = "text/plain;charset=utf-8";

    for(let i = 0; i< 1; i++)
    {
      let text = `{"p":"brc-20","op":"transfer","tick":"${mintData.ticker}","amt":"${parseFloat(mintData.inscribeAmount)}"}`;
      console.log(text);
      
      files.push({
        text: JSON.stringify(text),
        name: textToHex(text),
        hex: textToHex(text),
        mimetype: mimetype,
        sha256: ''
      });
    }

    let inscriptions = [];
    let inscriptionAddressList = [];

    for (let i = 0; i < files.length; i++) {

      const hex = files[i].hex;
      const data = hexToBytes(hex);
      const mimetype = ec.encode(files[i].mimetype);

      const script = [
          pubkey,
          'OP_CHECKSIG',
          'OP_0',
          'OP_IF',
          ec.encode('ord'),
          '01',
          mimetype,
          'OP_0',
          data,
          'OP_ENDIF'
      ];

      const script_backup = [
          '0x' + buf2hex(pubkey.buffer),
          'OP_CHECKSIG',
          'OP_0',
          'OP_IF',
          '0x' + buf2hex(ec.encode('ord')),
          '01',
          '0x' + buf2hex(mimetype),
          'OP_0',
          '0x' + buf2hex(data),
          'OP_ENDIF'
      ];

      const leaf = await Tap.tree.getLeaf(Script.encode(script));
      const [tapkey, cblock] = await Tap.getPubKey(pubkey, { target: leaf });

      let inscriptionAddress = Address.p2tr.encode(tapkey, encodedAddressPrefix);

      let prefix = 160;

      let txsize = prefix + Math.floor(data.length / 4);

      inscriptionAddressList.push(inscriptionAddress);

      inscriptions.push(
        {
          leaf: leaf,
          tapkey: tapkey,
          cblock: cblock,
          inscriptionAddress: inscriptionAddress,
          txsize: txsize,
          fee: fees.inscribeFee - padding,
          script: script_backup,
          script_orig: script
        }
      );
    }

    let fundTransactionId = mintData.transactionId;
    let fundInscriptionId = mintData.inscriptionId;

    if (mintData.mode == 1) {
      let outputs = [];

      let transaction = [];
      let transactionData;
      while(true) {
        transactionData = await getMempoolUtxos(mintData.fundingAddress);
        if (transactionData.length > 0) break;
        await sleep(5000);
      }
      // create fund transfer outputs
      outputs.push(
        {
          value: fees.inscribeFee,
          scriptPubKey: [ 'OP_1', inscriptions[0].tapkey ]
        }
      )

      outputs.push(
        {
          value: fees.transferFee,
          scriptPubKey: [ 'OP_1', Address.p2tr.decode(brc20ClaimOwnerAddress, encodedAddressPrefix).hex ]
        }
      );

      outputs.push(
        {
          value: fees.serviceFee,
          scriptPubKey: [ 'OP_1', Address.p2tr.decode(brc20ClaimFeeAddress, encodedAddressPrefix).hex ]
        }
      )

      const init_redeemtx = Tx.create({
        vin  : [{
            txid: transactionData[0].txid,
            vout: transactionData[0].vout,
            prevout: {
                value: transactionData[0].value,
                scriptPubKey: [ 'OP_1', init_tapkey ]
            },
        }],
        vout : outputs
      })

      const init_sig = await Signer.taproot.sign(seckey.raw, init_redeemtx, 0, {extension: init_leaf});
      init_redeemtx.vin[0].witness = [ init_sig.hex, init_script, init_cblock ];

      let rawtx = Tx.encode(init_redeemtx).hex;

      while(true) {
        let _txid = await broadcastBitcoinTransaction(rawtx);
        if (_txid.length == 64) {  // register
          await updateBrc20ClaimData({
            randomKey: mintData.privatekey,
            mode: 2, // transfer
            transactionId: _txid
          });
          fundTransactionId = _txid;
          break;
        }
        await sleep(5000);
      }
    }

    if (fundTransactionId != "") {
      // inscribe ordinal
      if (mintData.mode <= 2) {
        let _toAddress = Address.p2tr.decode(brc20ClaimOwnerAddress, encodedAddressPrefix).hex;
        let _script = [ 'OP_1', _toAddress ];

        const redeemtx = Tx.create({
          vin  : [{
              txid: fundTransactionId,
              vout: 0,
              prevout: {
                  value: fees.inscribeFee,
                  scriptPubKey: [ 'OP_1', inscriptions[0].tapkey ]
              },
          }],
          vout : [{
              value: padding,
              scriptPubKey: _script
          }],
        });

        const sig = await Signer.taproot.sign(seckey.raw, redeemtx, 0, {extension: inscriptions[0].leaf});
        redeemtx.vin[0].witness = [ sig.hex, inscriptions[0].script_orig, inscriptions[0].cblock ];
    
        let rawtx2 = Tx.encode(redeemtx).hex;
        let _txid2;
    
        while(true) {
          _txid2 = await broadcastBitcoinTransaction(rawtx2);
          if (_txid2.length == 64) {
            // save inscriptionId
            await updateBrc20ClaimData({
              randomKey: mintData.privatekey,
              mode: 3, // inscribe
              inscriptionId: _txid2
            });
            fundInscriptionId = _txid2;
            break;
          }
          await sleep(5000);
        }
      }

      // send inscription transfer

      let transactionParam = {
        utxos : [
          {
            txid : fundInscriptionId,
            vout : 0,
            value : padding,
          },
          {
            txid : fundTransactionId,
            vout : 1,
            value : fees.transferFee,
          }
        ],
        outputAddress : mintData.receiveAddress,
        outputAmount : padding,
      }

      let transactionData = await getBrc20ClaimTransactionData(transactionParam);

      while(true) {
        let txid = await broadcastBitcoinTransaction(transactionData.hex);
        if (txid.length == 64) {
          await updateBrc20ClaimData({
            randomKey: mintData.privatekey,
            mode: 4, // send
          });
          break;
        }
        await sleep(5000);
      }
    }
    
  }

  const handleRecover = async () => {
    while(true){
      try {
        let results = await getAllBrc20ClaimData();
        console.log(results);
        setLoading(true);

        for(let data of results) {
          await handleMint(data);
        }

        setLoading(false);
      }
      catch(error) {
        console.log(error);
      }
    
      await sleep(20000);
    }
  }

  const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  return (
    <div className="text-white mt-[65px] w-full max-w-[1500px] md:px-7 px-2 flex flex-col justify-center items-center mb-10 min-h-[600px]">
      <div className="w-[400px]">
        <Button
          onClick={() => {
            handleRecover();
          }}
          fullWidth
        >RecoverBrc20Claim</Button>
      </div>
      {
        loading ? <Spinner className="h-16 w-16 text-gray-900/50 fixed" color="pink"/> : <></>
      } 
      <Toaster 
        position="top-right"
      />
    </div>
  )
}
